/**
 * Slice interpolation utilities for filling gaps in DICOM series
 */

/**
 * Interpolation method for generating missing slices
 */
export enum InterpolationMethod {
    /** Simple linear interpolation between adjacent slices (fast, good for most cases) */
    Linear = 'Linear',
    /** Copy the nearest existing slice (fastest, useful for testing) */
    NearestNeighbor = 'NearestNeighbor',
}

/**
 * Options for Image3D construction with interpolation support
 */
export interface Image3DOptions {
    /** Enable interpolation of missing slices (default: false) */
    interpolateMissingSlices?: boolean;
    /** Interpolation algorithm to use (default: Linear) */
    interpolationMethod?: InterpolationMethod;
    /** Maximum number of consecutive missing slices to interpolate (default: 10) */
    maxInterpolatedSlices?: number;
}

/**
 * Interpolate a single slice between two existing slices
 *
 * @param sliceBelow Pixel data from the slice below (lower z)
 * @param sliceAbove Pixel data from the slice above (higher z)
 * @param weight Interpolation weight: 0.0 = sliceBelow, 1.0 = sliceAbove
 * @param method Interpolation algorithm to use
 * @returns Interpolated pixel data
 */
export function interpolateSlice(
    sliceBelow: Int16Array,
    sliceAbove: Int16Array,
    weight: number,
    method: InterpolationMethod
): Int16Array {
    if (sliceBelow.length !== sliceAbove.length) {
        throw new Error(
            `Slice size mismatch: ${sliceBelow.length} vs ${sliceAbove.length}`
        );
    }

    const result = new Int16Array(sliceBelow.length);

    switch (method) {
        case InterpolationMethod.Linear:
            for (let i = 0; i < result.length; i++) {
                result[i] = Math.round(
                    sliceBelow[i] * (1 - weight) + sliceAbove[i] * weight
                );
            }
            break;

        case InterpolationMethod.NearestNeighbor:
            result.set(weight < 0.5 ? sliceBelow : sliceAbove);
            break;

        default:
            throw new Error(`Unknown interpolation method: ${method}`);
    }

    return result;
}

/**
 * Information about a slice at a specific z-index
 */
export interface SliceInfo {
    /** Z-index in the volume */
    zIndex: number;
    /** Pixel data for the slice */
    pixelData: Int16Array;
}

/**
 * Find the nearest existing slices above and below a given z-index.
 * Sorts the input by zIndex internally to guarantee correctness.
 *
 * @param targetZ The z-index to find neighbors for
 * @param slices Array of existing slices (any order)
 * @returns Tuple of [sliceBelow, sliceAbove]
 */
export function findNearestSlices(
    targetZ: number,
    slices: SliceInfo[]
): [SliceInfo, SliceInfo] {
    let below: SliceInfo | undefined;
    let above: SliceInfo | undefined;

    const sorted = [...slices].sort((a, b) => a.zIndex - b.zIndex);
    for (const slice of sorted) {
        if (slice.zIndex < targetZ) {
            below = slice;
        } else if (slice.zIndex > targetZ) {
            above = slice;
            break;
        }
    }

    if (!below || !above) {
        throw new Error(
            `Cannot find surrounding slices for z=${targetZ}. ` +
                `Below: ${below?.zIndex}, Above: ${above?.zIndex}`
        );
    }

    return [below, above];
}

/**
 * Calculate the maximum consecutive gap in a series of z-indices
 *
 * @param existingIndices Sorted array of existing slice z-indices
 * @param totalSlices Total number of slices in the complete volume
 * @returns Maximum number of consecutive missing slices
 */
export function getMaxConsecutiveGap(
    existingIndices: number[],
    totalSlices: number
): number {
    if (existingIndices.length === 0) {
        return totalSlices;
    }

    let maxGap = 0;

    // Check gap at the beginning
    if (existingIndices[0] > 0) {
        maxGap = existingIndices[0];
    }

    // Check gaps between existing slices
    for (let i = 1; i < existingIndices.length; i++) {
        const gap = existingIndices[i] - existingIndices[i - 1] - 1;
        if (gap > maxGap) {
            maxGap = gap;
        }
    }

    // Check gap at the end
    const endGap =
        totalSlices - 1 - existingIndices[existingIndices.length - 1];
    if (endGap > maxGap) {
        maxGap = endGap;
    }

    return maxGap;
}
